// theme.js - Centralized Theme System for LedgerAI
import { createTheme, responsiveFontSizes } from "@mui/material/styles";

/**
 * Central color palette for the entire application
 * This is the single source of truth for all colors
 */
const colors = {
  // Brand Colors (Premium charcoal/sage palette)
  brand: {
    primary: "#1c1d1a",
    primaryLight: "#2a2b27",
    primaryDark: "#0f100e",
    primaryGradient: "linear-gradient(135deg, #1c1d1a 0%, #2a2b27 100%)",
  },

  // Accent Colors
  accent: {
    primary: "#3d5743",
    primaryAlt: "#2f4435",
    teal: "#3d5743",
    tealAlt: "#2f4435",
  },

  // Semantic Colors
  semantic: {
    success: "#4a7c59",
    error: "#b3493a",
    warning: "#c08a3e",
    info: "#3d5743",
  },

  // Chart-specific Colors
  charts: {
    positive: "#4a7c59",
    negative: "#b3493a",
    primary: "#3d5743",
    secondary: "#b3493a",
    teal: "#3d5743",
    tealAlt: "#2f4435",
    greenSoft: "#4a7c59",
    purple: "#3d5743",
    cyan: "#3d5743",
    neutralRef: "#e7e2d5",
  },

  // Grayscale Palette
  gray: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    500: "#6b7280",
    600: "#4b5563",
    700: "#374151",
    800: "#1f2937",
    900: "#111827",
  },
};

/**
 * Theme tokens organized by mode (dark/light)
 */
const tokens = {
  dark: {
    // Brand Colors
    primary: "#e7e2d5",
    primaryAlt: "#f5f3ee",
    primaryDark: "#cfc7b6",
    primaryGradient: "linear-gradient(135deg, #e7e2d5 0%, #f5f3ee 100%)",

    // Semantic Colors
    success: colors.semantic.success,
    error: colors.semantic.error,
    warning: colors.semantic.warning,
    info: colors.semantic.info,

    // Background Colors
    appBg: "#11130f",
    workspaceBg: "#151813",
    workspaceBorder: "#30372d",
    sidebarBg: "#0f100e",
    sidebarText: "#858d7f",
    sidebarTextActive: "#f5f3ee",
    sidebarActiveBg: "rgba(245, 243, 238, 0.08)",
    sidebarBorder: "rgba(245, 243, 238, 0.08)",
    sidebarLogoBg: "#080907",
    cardBg: "#1b1f19",
    cardBorder: "#30372d",
    headerBg: "#22271f",
    rowAltBg: "#20251e",
    chipBg: "#2a3027",
    chipText: "#d5d0c3",
    pillBg: "#263327",
    pillText: "#a9c7a4",
    badgeBg: "#263327",
    badgeText: "#c6ddc1",
    inputBg: "#22271f",
    inputBorder: "#3a4236",
    primaryText: "#11130f",
    primaryHover: "#f5f3ee",
    accent: "#8aa083",
    accentHover: "#a9c7a4",
    successBg: "#203226",
    danger: colors.semantic.error,
    dangerBg: "#3b221f",
    warningBg: "#3a2f1f",
    focusRing: "rgba(138, 160, 131, 0.35)",

    bgPrimary: "#11130f",
    bgSecondary: "#151813",
    bgDefault: "#11130f",
    bgPaper: "#1b1f19",
    bgCard: "#1b1f19",
    bgCardHover: "#20251e",
    bgSubtle: "rgba(245, 243, 238, 0.08)",
    bgOverlay: "rgba(138, 160, 131, 0.35)",

    // Text Colors
    textPrimary: "#f5f3ee",
    textSecondary: "#c7cbbf",
    textTertiary: "#b7bcae",
    textMuted: "#858d7f",
    textDisabled: "#6f766a",

    // Border Colors
    borderLight: "#30372d",
    borderMedium: "#3a4236",
    borderDark: "#4a5445",
    borderPrimary: "#465140",
    borderTable: "#30372d",
    divider: "#30372d",

    // Shadows
    shadowLight: "0 1px 2px rgba(0, 0, 0, 0.28)",
    shadowMedium: "0 8px 20px rgba(0, 0, 0, 0.24)",
    shadowHeavy: "0 16px 36px rgba(0, 0, 0, 0.36)",
    shadowButton: "0 6px 18px rgba(0, 0, 0, 0.24)",
    cardShadow: "0 1px 2px rgba(0, 0, 0, 0.28)",

    // Gradients & Effects
    cardGradient: "#1b1f19",
    cardBorderLine: "1px solid #30372d",

    // Charts
    charts: {
      ...colors.charts,
      greenSoft: "#4a7c59",
      purple: "#3d5743",
      cyan: "#3d5743",
    },
  },

  light: {
    // Brand Colors
    primary: colors.brand.primary,
    primaryAlt: colors.brand.primaryLight,
    primaryDark: colors.brand.primaryDark,
    primaryGradient: colors.brand.primaryGradient,

    // Semantic Colors
    success: colors.semantic.success,
    error: colors.semantic.error,
    warning: colors.semantic.warning,
    info: colors.semantic.info,

    // Background Colors
    appBg: "#f5f3ee",
    workspaceBg: "#fbfaf6",
    workspaceBorder: "#e7e2d5",
    sidebarBg: "#1c1d1a",
    sidebarText: "#9c9d96",
    sidebarTextActive: "#f5f3ee",
    sidebarActiveBg: "rgba(245, 243, 238, 0.06)",
    sidebarBorder: "rgba(245, 243, 238, 0.06)",
    sidebarLogoBg: "#0f100e",
    cardBg: "#ffffff",
    cardBorder: "#e7e2d5",
    headerBg: "#f3efe5",
    rowAltBg: "#faf8f2",
    chipBg: "#ebe6d8",
    chipText: "#5b5a4d",
    pillBg: "#e3ede4",
    pillText: "#3d5743",
    badgeBg: "#e1e6dc",
    badgeText: "#3d5743",
    inputBg: "#f3efe5",
    inputBorder: "#e0dac8",
    primaryText: "#fbfaf6",
    primaryHover: "#2a2b27",
    accent: "#3d5743",
    accentHover: "#2f4435",
    successBg: "#e3ede4",
    danger: colors.semantic.error,
    dangerBg: "#f4e4df",
    warningBg: "#f4ebd9",
    focusRing: "rgba(61, 87, 67, 0.35)",

    bgPrimary: "#f5f3ee",
    bgSecondary: "#fbfaf6",
    bgDefault: "#f5f3ee",
    bgPaper: "#ffffff",
    bgCard: "#ffffff",
    bgCardHover: "#faf8f2",
    bgSubtle: "rgba(245, 243, 238, 0.06)",
    bgOverlay: "rgba(61, 87, 67, 0.35)",

    // Text Colors
    textPrimary: "#1a2420",
    textSecondary: "#6b7268",
    textTertiary: "#6b7268",
    textMuted: "#9ca29a",
    textDisabled: "#9ca29a",

    // Border Colors
    borderLight: "#e7e2d5",
    borderMedium: "#e0dac8",
    borderDark: "#e7e2d5",
    borderPrimary: "#e7e2d5",
    borderTable: "#e7e2d5",
    divider: "#e7e2d5",

    // Shadows
    shadowLight: "0 1px 2px rgba(26, 36, 32, 0.04)",
    shadowMedium: "0 1px 2px rgba(26, 36, 32, 0.04)",
    shadowHeavy: "0 10px 24px rgba(26, 36, 32, 0.08)",
    shadowButton: "0 1px 2px rgba(26, 36, 32, 0.04)",
    cardShadow: "0 1px 2px rgba(26, 36, 32, 0.04)",

    // Gradients & Effects
    cardGradient: "#ffffff",
    cardBorderLine: "1px solid #e7e2d5",

    // Charts
    charts: {
      ...colors.charts,
      greenSoft: "#4a7c59",
      purple: "#3d5743",
      cyan: "#3d5743",
    },
  },
};

/**
 * Utility function to update CSS custom properties for theme switching
 * Note: Base properties are now set in index.html to prevent FOUC
 */
const updateCSSCustomProperties = (tokens) => {
  if (typeof document === "undefined") return; // Handle SSR

  const root = document.documentElement;

  const aliases = {
    "--app-bg": tokens.appBg,
    "--workspace-bg": tokens.workspaceBg,
    "--workspace-border": tokens.workspaceBorder,
    "--sidebar-bg": tokens.sidebarBg,
    "--sidebar-text": tokens.sidebarText,
    "--sidebar-text-active": tokens.sidebarTextActive,
    "--sidebar-active-bg": tokens.sidebarActiveBg,
    "--sidebar-border": tokens.sidebarBorder,
    "--sidebar-logo-bg": tokens.sidebarLogoBg,
    "--card-bg": tokens.cardBg || tokens.bgCard,
    "--card-border": tokens.cardBorder,
    "--header-bg": tokens.headerBg,
    "--row-alt-bg": tokens.rowAltBg || tokens.bgCardHover,
    "--text": tokens.textPrimary,
    "--text-muted": tokens.textSecondary,
    "--text-faint": tokens.textMuted,
    "--chip-bg": tokens.chipBg,
    "--chip-text": tokens.chipText,
    "--pill-bg": tokens.pillBg,
    "--pill-text": tokens.pillText,
    "--badge-bg": tokens.badgeBg,
    "--badge-text": tokens.badgeText,
    "--input-bg": tokens.inputBg,
    "--input-border": tokens.inputBorder,
    "--primary-text": tokens.primaryText,
    "--primary-hover": tokens.primaryHover,
    "--accent-hover": tokens.accentHover,
    "--success-bg": tokens.successBg,
    "--danger-bg": tokens.dangerBg,
    "--warning-bg": tokens.warningBg,
    "--focus-ring": tokens.focusRing,
    "--cardGradient": tokens.cardGradient,
    "--cardBorder": tokens.cardBorderLine || tokens.cardBorder,
  };

  Object.entries(aliases).forEach(([key, value]) => {
    if (value) root.style.setProperty(key, value);
  });

  // Only update the colors that might change between themes.
  // Core design tokens (spacing, radius, etc.) are set in index.html.
  Object.entries(tokens).forEach(([key, value]) => {
    if (typeof value === "object" && value !== null) {
      // Handle nested objects (like charts)
      Object.entries(value).forEach(([subKey, subValue]) => {
        root.style.setProperty(`--${key}-${subKey}`, subValue);
      });
    } else {
      // Handle direct values
      root.style.setProperty(`--${key}`, value);
    }
  });
};

/**
 * Factory to build the MUI theme with our custom tokens.
 * Usage:
 *   const theme = makeAppTheme('dark') // or 'light'
 */
export const makeAppTheme = (mode = "light") => {
  const t = tokens[mode];

  // Update CSS custom properties for the current mode
  // Base properties are already set in index.html to prevent FOUC
  updateCSSCustomProperties(t);

  let theme = createTheme({
    palette: {
      mode,
      primary: {
        main: t.primary,
        light: t.primaryAlt,
        dark: t.primaryDark,
      },
      secondary: {
        main: t.primaryAlt,
        light: colors.accent.primary,
        dark: colors.accent.primaryAlt,
      },
      success: { main: t.success },
      error: { main: t.error },
      warning: { main: t.warning },
      info: { main: t.info },

      background: {
        default: t.bgDefault,
        paper: t.bgPaper,
      },
      text: {
        primary: t.textPrimary,
        secondary: t.textSecondary,
      },
      divider: t.divider,

      common: {
        white: "#ffffff",
        black: t.primary,
      },

      // Custom palette extensions
      custom: {
        brand: {
          primary: t.primary,
          primaryLight: t.primaryAlt,
          primaryDark: t.primaryDark,
          gradient: t.primaryGradient,
        },
        background: {
          primary: t.bgPrimary,
          secondary: t.bgSecondary,
          card: t.bgCard,
          cardHover: t.bgCardHover,
          subtle: t.bgSubtle,
          overlay: t.bgOverlay,
        },
        text: {
          tertiary: t.textTertiary,
          muted: t.textMuted,
          disabled: t.textDisabled,
        },
        border: {
          light: t.borderLight,
          medium: t.borderMedium,
          dark: t.borderDark,
          primary: t.borderPrimary,
          table: t.borderTable,
        },
        shadow: {
          light: t.shadowLight,
          medium: t.shadowMedium,
          heavy: t.shadowHeavy,
          button: t.shadowButton,
          card: t.cardShadow,
        },
        charts: { ...t.charts },
      },
    },

    // Legacy support for existing code
    cj: {
      pageBg: t.bgPrimary,
      cardGradient: t.cardGradient,
      cardShadow: t.cardShadow,
      cardBorder: t.cardBorderLine || `1px solid ${t.cardBorder}`,
      charts: { ...t.charts },
    },

    shape: {
      borderRadius: 12,
    },

    spacing: 8, // Base spacing unit (8px)

    typography: {
      fontFamily: [
        '"Hanken Grotesk"',
        "system-ui",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "sans-serif",
      ].join(","),
      h1: { color: t.textPrimary, fontWeight: 800, fontSize: "2.5rem", letterSpacing: "-0.02em" },
      h2: { color: t.textPrimary, fontWeight: 800, fontSize: "2rem", letterSpacing: "-0.02em" },
      h3: { color: t.textPrimary, fontWeight: 800, fontSize: "1.75rem", letterSpacing: "-0.02em" },
      h4: { color: t.textPrimary, fontWeight: 800, fontSize: "1.5rem", letterSpacing: "-0.02em" },
      h5: { color: t.textPrimary, fontWeight: 700, fontSize: "1.25rem", letterSpacing: "-0.02em" },
      h6: { color: t.textPrimary, fontWeight: 700, fontSize: "1.125rem", letterSpacing: "-0.02em" },
      body1: { color: t.textPrimary, fontWeight: 400, fontSize: "0.875rem", lineHeight: 1.5 },
      body2: { color: t.textPrimary, fontWeight: 400, fontSize: "0.8125rem", lineHeight: 1.5 },
      caption: { fontWeight: 400, fontSize: "0.75rem" },
      button: { fontWeight: 600, textTransform: "none" },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.bgDefault,
            color: t.textPrimary,
            fontSize: "14px",
            lineHeight: 1.5,
          },
          "#analytics-dashboard": {
            backgroundColor: t.bgDefault,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: t.bgCard,
            border: `1px solid ${t.borderLight}`,
            borderRadius: 12,
            boxShadow: t.cardShadow,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: t.bgCard,
            border: `1px solid ${t.borderLight}`,
            boxShadow: t.cardShadow,
            borderRadius: 12,
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: t.borderPrimary,
              boxShadow: t.cardShadow,
            },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
            boxShadow: "none",
          },
          containedPrimary: {
            background: t.primary,
            color: t.primaryText,
            boxShadow: "none",
            "&:hover": {
              background: t.primaryHover,
              boxShadow: "none",
            },
          },
          outlinedPrimary: {
            borderColor: t.borderLight,
            color: t.textPrimary,
            "&:hover": {
              backgroundColor: t.bgCardHover,
              borderColor: t.borderMedium,
            },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: 999,
            backgroundColor: t.chipBg,
            color: t.chipText,
          },
          colorPrimary: {
            backgroundColor: t.pillBg,
            color: t.pillText,
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: t.inputBg,
              borderRadius: 8,
              "& fieldset": {
                borderColor: t.inputBorder,
              },
              "&:hover fieldset": {
                borderColor: t.inputBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: t.accent,
                borderWidth: "2px",
                boxShadow: `0 0 0 2px ${t.focusRing}`,
              },
            },
            "& .MuiInputLabel-root": {
              color: t.textMuted,
              "&.Mui-focused": {
                color: t.accent,
              },
            },
            "& .MuiInputBase-input": {
              color: t.textPrimary,
            },
          },
        },
      },

      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: t.bgCard,
            borderRadius: 12,
            border: `1px solid ${t.borderTable}`,
            boxShadow: t.cardShadow,
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: t.headerBg,
            "& .MuiTableCell-head": {
              backgroundColor: t.headerBg,
              color: t.textSecondary,
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              borderBottom: `1px solid ${t.borderMedium}`,
            },
          },
        },
      },

      MuiTableBody: {
        styleOverrides: {
          root: {
            "& .MuiTableRow-root": {
              "&:nth-of-type(even)": {
                backgroundColor: t.rowAltBg,
              },
              "&:nth-of-type(odd)": {
                backgroundColor: t.bgCard,
              },
              "&:hover": {
                backgroundColor: t.bgCardHover,
              },
            },
            "& .MuiTableCell-root": {
              color: t.textSecondary,
              borderBottom: `1px solid ${t.borderLight}`,
              fontVariantNumeric: "tabular-nums",
            },
          },
        },
      },
    },
  });

  // Make typography responsive
  theme = responsiveFontSizes(theme);
  return theme;
};

/**
 * Export the color palette for direct access
 */
export { colors };

/**
 * Export theme tokens for direct access
 */
export { tokens };

/**
 * Utility hook for accessing theme colors in components
 */
export const useThemeColors = (mode = "light") => {
  return tokens[mode];
};

/**
 * Export specific color palettes for easy access
 */
export const brandColors = colors.brand;
export const semanticColors = colors.semantic;
export const chartColors = colors.charts;

// Export a default theme instance
const theme = makeAppTheme("light");
export default theme;
